/**
 * Lab playground: generate new images with a Lab results entry's recipe on our own GPU towers.
 *
 * The dashboard API (`/lab-playground/*`, administrators only) writes job objects to the Labs bucket;
 * a lab worker on an H3 tower renders them under the H3 device lease, so production video always
 * goes first. Everything here is pure (types, payloads, grouping, wording) so it is unit-tested.
 */

export type PlaygroundJobState =
  | "queued"
  | "waiting_gpu"
  | "running"
  | "done"
  | "failed"
  | "cancelled"
  | "stalled";

export type PlaygroundJob = {
  job_id: string;
  batch_id: string;
  experiment: string;
  photo_id: string | null;
  lora: { id: string; label?: string; file: string | null } | null;
  strength: number;
  prompt_id: string;
  prompt: string;
  seed: number;
  steps: number;
  created_at: string;
  created_by?: string | null;
  state: PlaygroundJobState;
  message?: string | null;
  worker_id?: string | null;
  gpu_wait_seconds?: number | null;
  render_seconds?: number | null;
  deferred_to_h3?: boolean;
  input_url?: string | null;
  output_url?: string | null;
  output?: { width?: number; height?: number } | null;
};

export type PlaygroundWorker = {
  worker_id: string;
  state: string;
  online: boolean;
  gpu?: string | null;
  deferred_to_h3?: boolean;
  current_job_id?: string | null;
  loras?: string[];
  age_seconds?: number | null;
  jobs_done?: number | null;
};

export type PlaygroundStatus = {
  environment: string;
  online: boolean;
  busy: boolean;
  deferred_to_h3: boolean;
  queue_depth: number;
  workers: PlaygroundWorker[];
};

export type PlaygroundRequest = {
  experiment: string;
  photo_id?: string;
  upload_key?: string;
  lora_id: string;
  prompt_id?: string;
  custom_prompt?: string;
  strengths: number[];
  seed?: number;
};

export const ACTIVE_STATES: PlaygroundJobState[] = [
  "queued",
  "waiting_gpu",
  "running",
];

export const isActive = (job: Pick<PlaygroundJob, "state">) =>
  ACTIVE_STATES.includes(job.state);

/** Round to the slider's step so 0.8000000001 never reaches the API. */
export const roundStrength = (value: number, step = 0.05) =>
  Math.round(Math.round(value / step) * step * 1000) / 1000;

export type SeedMode = "published" | "random" | "fixed";

/** The request the Generate / Strength-sweep buttons send. */
export function buildRequest(input: {
  experiment: string;
  source: { kind: "photo"; photoId: string } | { kind: "upload"; key: string };
  loraId: string;
  promptId: string;
  customPrompt?: string;
  strengths: number[];
  seedMode: SeedMode;
  publishedSeed?: number;
  fixedSeed?: number;
}): PlaygroundRequest {
  const request: PlaygroundRequest = {
    experiment: input.experiment,
    lora_id: input.loraId,
    strengths: input.strengths.map((s) => roundStrength(s, 0.001)),
  };
  if (input.source.kind === "photo") request.photo_id = input.source.photoId;
  else request.upload_key = input.source.key;
  if (input.promptId === "custom")
    request.custom_prompt = (input.customPrompt ?? "").trim();
  else request.prompt_id = input.promptId;
  if (input.seedMode === "published" && input.publishedSeed !== undefined)
    request.seed = input.publishedSeed;
  if (input.seedMode === "fixed" && input.fixedSeed !== undefined)
    request.seed = input.fixedSeed;
  return request;
}

export type PlaygroundBatch = {
  batch_id: string;
  created_at: string;
  jobs: PlaygroundJob[];
};

/** History grouped by batch (one Generate click), newest batch first, strengths ascending inside. */
export function groupBatches(jobs: PlaygroundJob[]): PlaygroundBatch[] {
  const batches = new Map<string, PlaygroundBatch>();
  for (const job of jobs) {
    const batch = batches.get(job.batch_id) ?? {
      batch_id: job.batch_id,
      created_at: job.created_at,
      jobs: [],
    };
    batch.jobs.push(job);
    batches.set(job.batch_id, batch);
  }
  return [...batches.values()]
    .map((batch) => ({
      ...batch,
      jobs: [...batch.jobs].sort((a, b) => a.strength - b.strength),
    }))
    .sort((a, b) => b.batch_id.localeCompare(a.batch_id));
}

/** One line for the GPU status pill. */
export function describeStatus(status: PlaygroundStatus | undefined): {
  tone: "ok" | "busy" | "offline" | "unknown";
  key: string;
  gpu?: string;
} {
  if (!status) return { tone: "unknown", key: "status_unknown" };
  const online = status.workers.filter((w) => w.online);
  const gpu = online[0]?.gpu ?? undefined;
  if (!online.length) return { tone: "offline", key: "status_offline" };
  if (status.deferred_to_h3)
    return { tone: "busy", key: "status_production", gpu };
  if (status.busy) return { tone: "busy", key: "status_rendering", gpu };
  return { tone: "ok", key: "status_ready", gpu };
}

/** The translation key for a job's state line. */
export function stateKey(job: Pick<PlaygroundJob, "state" | "deferred_to_h3">) {
  if (job.state === "waiting_gpu" && job.deferred_to_h3)
    return "state_waiting_production";
  return `state_${job.state}`;
}

export const elapsedSeconds = (createdAt: string, now: number) => {
  const started = Date.parse(createdAt);
  return Number.isFinite(started)
    ? Math.max(0, Math.round((now - started) / 1000))
    : 0;
};
